// One-shot retirement of the old client-local unit toggle.
//
// Before ADR 0189 the choice was a binary metric/imperial flag in
// localStorage under `nm.unitPreference`, per browser and per device. It is now
// seven categories on the account. This hook moves a returning user's old
// choice up to the server exactly once, then deletes the key so the old
// mechanism is gone for good.
//
// The anti-clobber guard is the SERVER value being null — never a localStorage
// flag. `unit_settings` is null only while an account has never set its units
// (cloud migration 0080), so a deliberate choice made on any device can never
// be overwritten by a stale `imperial` left in some other browser's storage.
// A localStorage "already migrated" marker would guard the wrong thing: it is
// per-browser, so a second browser would still clobber the account.
//
// Crucially, that guard is EVALUATED BY THE SERVER, inside the write
// (`POST /v1/me/unit-settings/import-legacy`, `WHERE unit_settings IS NULL`).
// The `user.unitSettings === null` test below is only a cheap way to skip the
// request — it is read from a `/v1/me` fetched earlier, so it cannot be the
// real guard: a user who picks units on their phone while this tab sits open
// would have that deliberate choice overwritten by this browser's stale
// `imperial`. Check-then-act across devices is not a race the client can win,
// so it does not try.
//
// An account that has never chosen and has no old choice in this browser gets
// its default from the server instead (TOM-744, ADR 0409): this browser's time
// zone goes to `POST /v1/me/unit-settings/default`, which saves
// imperial for an owner who appears to be in the US and metric otherwise, under
// the same `WHERE unit_settings IS NULL` guard. An old 'metric' in this browser
// is an old choice too, so it is imported like 'imperial'.

import { useEffect, useRef } from 'react';
import { unitSettingsFromPreset } from '@neomaris/protocol';

import { api } from '../api';
import { useAuth } from '../auth';
import { deviceUnitSignals } from './deviceUnitSignals';

const LEGACY_KEY = 'nm.unitPreference';

/** This browser's old choice, if it holds one. */
function legacyChoice(): 'imperial' | 'metric' | null {
  try {
    const raw = window.localStorage.getItem(LEGACY_KEY);
    return raw === 'imperial' || raw === 'metric' ? raw : null;
  } catch {
    return null;
  }
}

function forgetLegacyKey(): void {
  try {
    window.localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Private mode, or no storage at all. Nothing to forget then.
  }
}

/**
 * Mount ONCE, inside the authenticated tree. Does nothing until `/v1/me` has
 * answered, then runs at most once per page load.
 */
export function useLegacyUnitMigration(): void {
  const { user, refresh } = useAuth();
  // Set before the first await, not after: StrictMode mounts effects twice in
  // development, and the second pass must not fire a second PATCH.
  const ran = useRef(false);

  useEffect(() => {
    if (user === null || ran.current) return;
    ran.current = true;

    if (user.unitSettings !== null) {
      forgetLegacyKey();
      return;
    }
    const legacy = legacyChoice();

    void (async () => {
      try {
        if (legacy === null) {
          // No old choice: the server decides the default from this device.
          await api('/v1/me/unit-settings/default', {
            method: 'POST',
            body: deviceUnitSignals(),
          });
          await refresh();
          return;
        }
        await api('/v1/me/unit-settings/import-legacy', {
          method: 'POST',
          body: { unitSettings: unitSettingsFromPreset(legacy) },
        });
        await refresh();
        // The server has answered definitively — it either took this
        // preference or told us the account had already chosen. Either way the
        // legacy key has done its job and must go, or it would keep trying
        // against an account that is settled. Only a transport failure leaves
        // it in place (the catch below), so a request that never landed can
        // still be retried on the next page load rather than losing the
        // preference permanently.
        forgetLegacyKey();
      } catch {
        ran.current = false;
      }
    })();
  }, [user, refresh]);
}
